import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  TextField,
  DialogContentText
} from '@mui/material';
import {
  ArrowUpward as PromoteIcon,
  ArrowDownward as DemoteIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import LoadingIndicator from './LoadingIndicator';
import InviteLinkPanel from './InviteLinkPanel';
import { useAuth } from '../contexts/AuthContext';
import { useAdmin } from '../contexts/AdminContext';
import { getProfile } from '../services/authApiService';
import {
  listOrganizationUsers,
  promoteUserToManager,
  demoteManagerToUser,
  deleteUser
} from '../services/userManagementService';
import linesOverlay from '../assets/images/Lines-no_background.png';

const heroBoxStyles = {
  backgroundColor: '#021c2d',
  backgroundImage: `url(${linesOverlay})`,
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  color: 'white',
  padding: { xs: 1.5, md: 2 },
  position: 'relative',
  zIndex: 1,
  borderRadius: '16px 16px 0 0'
};

const contentBoxStyles = {
  padding: { xs: 3, md: 5 },
  backgroundColor: 'white',
  borderRadius: '0 0 16px 16px'
};

const OrganizationUsers = () => {
  const { user, isDemoMode } = useAuth();
  const { isAdmin } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [currentUserOrgId, setCurrentUserOrgId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, user: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        // Check if user is admin, manager, or demo
        const userProfile = await getProfile();
        const isManager = userProfile.access_level === 'manager' || userProfile.access_level === 'demo';
        const userOrgId = userProfile.org_id;

        if (!isAdmin && !isManager) {
          setHasAccess(false);
          setLoading(false);
          return;
        }

        setHasAccess(true);
        setCurrentUserOrgId(userOrgId);
        setCurrentUserId(userProfile.user_id);

        // Fetch users in organization
        const orgUsers = await listOrganizationUsers(userOrgId);
        setUsers(orgUsers);

      } catch (err) {
        console.error('Failed to fetch users:', err);
        setError(err.message || 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, isAdmin]);

  const handlePromote = (targetUser) => {
    setConfirmDialog({
      open: true,
      action: 'promote',
      user: targetUser
    });
  };

  const handleDemote = (targetUser) => {
    setConfirmDialog({
      open: true,
      action: 'demote',
      user: targetUser
    });
  };

  const handleDelete = (targetUser) => {
    setDeleteDialog({
      open: true,
      user: targetUser
    });
    setDeleteConfirmEmail('');
  };

  const executeAction = async () => {
    const { action, user: targetUser } = confirmDialog;

    if (!targetUser || !currentUserOrgId) return;

    try {
      setProcessing(true);
      setError(null);

      if (action === 'promote') {
        await promoteUserToManager(currentUserOrgId, targetUser.user_id);
        setSuccess(`Successfully promoted ${targetUser.email} to manager`);
      } else if (action === 'demote') {
        await demoteManagerToUser(currentUserOrgId, targetUser.user_id);
        setSuccess(`Successfully demoted ${targetUser.email} to user`);
      }

      // Refresh user list
      const orgUsers = await listOrganizationUsers(currentUserOrgId);
      setUsers(orgUsers);

      setConfirmDialog({ open: false, action: null, user: null });

      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);

    } catch (err) {
      console.error(`Failed to ${action} user:`, err);
      setError(err.message || `Failed to ${action} user`);
      setConfirmDialog({ open: false, action: null, user: null });
    } finally {
      setProcessing(false);
    }
  };

  const executeDelete = async () => {
    const { user: targetUser } = deleteDialog;

    if (!targetUser || !currentUserOrgId) return;

    // Validate email confirmation
    if (deleteConfirmEmail !== targetUser.email) {
      setError('Email does not match. Please type the exact email address.');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      await deleteUser(currentUserOrgId, targetUser.user_id);
      setSuccess(`Successfully deleted ${targetUser.email} and all associated data`);

      // Refresh user list
      const orgUsers = await listOrganizationUsers(currentUserOrgId);
      setUsers(orgUsers);

      setDeleteDialog({ open: false, user: null });
      setDeleteConfirmEmail('');

      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);

    } catch (err) {
      console.error('Failed to delete user:', err);
      setError(err.message || 'Failed to delete user');
      setDeleteDialog({ open: false, user: null });
      setDeleteConfirmEmail('');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleDateString('fi-FI');
    } catch {
      return dateString;
    }
  };

  const getAccessLevelChip = (accessLevel) => {
    const level = (accessLevel || 'user').toLowerCase();
    return (
      <Chip
        label={level}
        color={
          level === 'admin' ? 'error' :
          level === 'manager' ? 'warning' :
          'default'
        }
        size="small"
      />
    );
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  if (!hasAccess) {
    return (
      <Alert severity="error">
        <Typography variant="h6" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1">
          Only organisation managers and administrators can access user management.
        </Typography>
      </Alert>
    );
  }

  const managerCount = users.filter(u => (u.access_level || 'user').toLowerCase() === 'manager').length;

  const formContainerStyles = {
    padding: { xs: 3, md: 5 },
    backgroundColor: 'white',
    borderRadius: '0 0 16px 16px'
  };

  return (
    <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid rgba(0, 0, 0, 0.12)' }}>
      <Box sx={heroBoxStyles}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: 'Bebas Neue',
            letterSpacing: 1
          }}
        >
          User Management
        </Typography>
      </Box>

      <Box sx={formContainerStyles}>
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(false)}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Show invite link panel for managers and admins (not demo) */}
        {(isAdmin || hasAccess) && currentUserId && !isDemoMode && (
          <InviteLinkPanel userId={currentUserId} orgId={currentUserOrgId} />
        )}

        <Typography variant="body2" sx={{ color: '#6b7685', mb: 3 }}>
          {users.length} total, {managerCount} {managerCount === 1 ? 'manager' : 'managers'}
        </Typography>

        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Last Login</strong></TableCell>
              <TableCell><strong>Access Level</strong></TableCell>
              <TableCell align="center"><strong>Edit</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No users found in this organisation.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((orgUser) => {
                const isCurrentUser = orgUser.user_id === currentUserId;
                const accessLevel = (orgUser.access_level || 'user').toLowerCase();
                const isManager = accessLevel === 'manager';
                const isOnlyManager = isManager && managerCount === 1;

                return (
                  <TableRow
                    key={orgUser.user_id}
                    hover
                    sx={{ backgroundColor: isCurrentUser ? 'rgba(0, 0, 0, 0.04)' : 'inherit' }}
                  >
                    <TableCell>
                      {orgUser.email}
                    </TableCell>
                    <TableCell>{formatDate(orgUser.last_seen)}</TableCell>
                    <TableCell>{getAccessLevelChip(orgUser.access_level)}</TableCell>
                    <TableCell align="center">
                      {accessLevel !== 'admin' && !isCurrentUser && !isDemoMode && (
                        <>
                          {!isManager && (
                            <Tooltip title="Promote to Manager">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handlePromote(orgUser)}
                                sx={{ mr: 0.5 }}
                              >
                                <PromoteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isManager && (
                            <Tooltip title={isOnlyManager ? "Cannot demote last manager" : "Demote to User"}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleDemote(orgUser)}
                                  disabled={isOnlyManager}
                                  sx={{ mr: 0.5 }}
                                >
                                  <DemoteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {isAdmin && (
                            <Tooltip title={isOnlyManager ? "Cannot delete last manager" : "Delete User"}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(orgUser)}
                                  disabled={isOnlyManager}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </>
                      )}
                      {accessLevel === 'admin' && (
                        <Typography variant="caption" color="text.secondary">
                          Admin
                        </Typography>
                      )}
                      {isCurrentUser && accessLevel !== 'admin' && (
                        <Typography variant="caption" color="text.secondary">
                          Cannot modify yourself
                        </Typography>
                      )}
                      {isDemoMode && accessLevel !== 'admin' && !isCurrentUser && (
                        <Typography variant="caption" color="text.secondary">
                          Editing disabled in demo mode
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => !processing && setConfirmDialog({ open: false, action: null, user: null })}
      >
        <DialogTitle>
          {confirmDialog.action === 'promote' ? 'Promote User to Manager?' : 'Demote Manager to User?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {confirmDialog.action} <strong>{confirmDialog.user?.email}</strong>?
          </Typography>
          {confirmDialog.action === 'promote' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                This user will gain access to:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Organisation settings and user management</li>
                <li>Organisation wide and advanced reports (all results)</li>
              </ul>
            </Alert>
          )}
          {confirmDialog.action === 'demote' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                This user will lose access to:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Organisation settings and user management</li>
                <li>Organisational reports (will only see own results)</li>
              </ul>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, action: null, user: null })}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={executeAction}
            disabled={processing}
            sx={{
              backgroundColor: '#021c2d',
              '&:hover': { backgroundColor: '#042a44' }
            }}
          >
            {processing ? <CircularProgress size={20} color="inherit" /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog - Requires Email Input */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => !processing && setDeleteDialog({ open: false, user: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
          ⚠️ Delete User - Permanent Action
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You are about to <strong>permanently delete</strong>:
          </DialogContentText>
          <Box sx={{
            backgroundColor: '#fff3e0',
            p: 2,
            borderRadius: 1,
            border: '1px solid #ff9800',
            mb: 2
          }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {deleteDialog.user?.email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {deleteDialog.user?.first_name} {deleteDialog.user?.last_name}
            </Typography>
          </Box>

          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              This will permanently delete:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>User profile and account</li>
              <li>All interview sessions</li>
              <li>All assessment data and results</li>
              <li>All associated analytics</li>
            </ul>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1, color: '#d32f2f' }}>
              This action cannot be undone!
            </Typography>
          </Alert>

          <DialogContentText sx={{ mb: 2, fontWeight: 'bold' }}>
            To confirm, please type the user's email address:
          </DialogContentText>
          <TextField
            fullWidth
            label="Type email to confirm"
            value={deleteConfirmEmail}
            onChange={(e) => setDeleteConfirmEmail(e.target.value)}
            autoFocus
            disabled={processing}
            error={deleteConfirmEmail !== '' && deleteConfirmEmail !== deleteDialog.user?.email}
            helperText={
              deleteConfirmEmail !== '' && deleteConfirmEmail !== deleteDialog.user?.email
                ? 'Email does not match'
                : ''
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setDeleteDialog({ open: false, user: null });
              setDeleteConfirmEmail('');
            }}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={executeDelete}
            disabled={processing || deleteConfirmEmail !== deleteDialog.user?.email}
          >
            {processing ? <CircularProgress size={20} color="inherit" /> : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Paper>
  );
};

export default OrganizationUsers;
